import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
    getNotification_action,
    readNotification_action,
    getNotificationPreview_action,
    getTaskNotification_action,
    readTaskNotification_action,
    getTaskNotificationPreview_action,
} from '../actions/notification_action/notification_action';

const STATIC_NOTIFICATIONS = [
];

// A notification raised by someone else's action has to arrive without the user
// reloading the page, so the list is refetched in the background
const REFRESH_INTERVAL_MS = 15000;

// First alphanumeric character of the subject, used as the avatar letter.
function getInitial(text) {
    const plain = String(text ?? "").replace(/<[^>]*>/g, "").trim();
    const match = plain.match(/[A-Za-z0-9]/);
    return (match ? match[0] : plain.charAt(0) || "?").toUpperCase();
}

function formatSentDate(value) {
    if (!value) return "";
    // Show the value exactly as sent (treat the Z time as-is, no local shift).
    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : value;
}

// The two sources are ordered against each other, so each item keeps the raw
// timestamp alongside the printed one
function sentAtValue(value) {
    if (!value) return 0;
    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed.valueOf() : 0;
}

// The API returns every notification, read or not; ReadDate is null until read.
function isUnread(item) {
    const readDate = item.ReadDate ?? item.ReadedDate ?? item.ReadOn;
    if (readDate == null || String(readDate).trim() === "") return true;
    // Some endpoints send the SQL default instead of null for "never read".
    return String(readDate).startsWith("0001-01-01");
}

function mapApiNotification(item, index) {
    const subject = item.Subject ?? item.NotificationMessage ?? "New notification";
    const sentAt = item.SentDate ?? item.CreatedAt ?? item.NotificationTime ?? item.Time;
    return {
        id: item.IntimationNotificationID ?? index + 1,
        // Which store the row came from decides which endpoint reads and previews it
        source: 'intimation',
        intimationNotificationID: item.IntimationNotificationID ?? null,
        // Needed to fetch the full HTML body (with tables) from the preview endpoint.
        feaIntimationRecipientID: item.FeaIntimationRecipientID ?? item.FeaIntimationRecipientId ?? null,
        title: subject,
        initial: getInitial(subject),
        exhRegistrationNo: item.ExhRegistrationNo ?? item.ExhRegistrationNumber ?? null,
        companyName: item.CompanyName ?? null,
        body: item.Body ?? item.MessageBody ?? item.EmailBody ?? item.BodyHtml ?? item.Content ?? null,
        time: formatSentDate(sentAt),
        sentAt: sentAtValue(sentAt),
        unread: isUnread(item),
        type: "info",
    };
}

// A task notification carries no company / application number, so the meta line
// under the subject is used for where the work is instead
function mapTaskNotification(item) {
    const subject = item.Subject ?? "Task update";
    const meta = [item.CompanyName, item.HallName, item.ServiceName]
        .filter(Boolean)
        .join(' • ');

    return {
        // Prefixed so it can never collide with an intimation's numeric id
        id: `task_${item.VendorTaskNotificationRecipientID}`,
        source: 'task',
        taskRecipientID: item.VendorTaskNotificationRecipientID,
        title: subject,
        initial: getInitial(subject),
        exhRegistrationNo: null,
        companyName: meta || null,
        // The body is always fetched from the preview endpoint, wrapped in the
        // exhibition's mail header and footer exactly as it was mailed
        body: null,
        time: formatSentDate(item.SentDate),
        sentAt: sentAtValue(item.SentDate),
        unread: isUnread(item),
        type: "info",
    };
}

// The preview endpoint may return the HTML body under any of these shapes.
function extractPreview(res) {
    const root = res?.data ?? res;
    const obj = Array.isArray(root) ? root[0] : root;
    return {
        body:
            obj?.FullHtml ?? obj?.HtmlContent ?? obj?.Body ?? obj?.MessageBody ?? obj?.EmailBody ??
            obj?.BodyHtml ?? obj?.Content ?? null,
        exhRegistrationNo: obj?.ExhRegistrationNo ?? obj?.ExhRegistrationNumber ?? null,
        companyName: obj?.CompanyName ?? null,
    };
}

function asList(res) {
    const list = res?.data ?? res;
    return Array.isArray(list) ? list : [];
}

const useNotifications = () => {
    const dispatch = useDispatch();
    const [notifications, setNotifications] = useState([]);
    const [previewItem, setPreviewItem] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    // Read is marked on the server one call per notification. A refresh landing
    // while those are still in flight would read the old ReadDate back and light
    // the item up again, so what was read here is remembered for the session
    const locallyReadIds = useRef(new Set());
    // At this interval a slow round trip can still be in flight when the next
    // tick fires. Stacking them up would only queue duplicate calls, so a tick
    // that finds one running is skipped
    const isLoadingRef = useRef(false);

    const loadNotifications = useCallback(() => {
        if (isLoadingRef.current) return Promise.resolve();
        isLoadingRef.current = true;

        // Two independent stores. One being down must not blank the other out
        return Promise.allSettled([
            dispatch(getNotification_action()),
            dispatch(getTaskNotification_action()),
        ]).then(([intimationRes, taskRes]) => {
            const intimations =
                intimationRes.status === 'fulfilled' ? asList(intimationRes.value) : [];
            const tasks = taskRes.status === 'fulfilled' ? asList(taskRes.value) : [];

            if (intimationRes.status === 'rejected' && taskRes.status === 'rejected') {
                // A failed refresh keeps whatever is already on screen
                setNotifications((prev) => (prev.length > 0 ? prev : STATIC_NOTIFICATIONS));
                return;
            }

            const merged = [
                ...intimations.map(mapApiNotification),
                ...tasks.map(mapTaskNotification),
            ]
                .map((n) => (locallyReadIds.current.has(n.id) ? { ...n, unread: false } : n))
                .sort((a, b) => b.sentAt - a.sentAt);

            setNotifications(merged.length > 0 ? merged : STATIC_NOTIFICATIONS);
        }).finally(() => {
            isLoadingRef.current = false;
        });
    }, [dispatch]);

    useEffect(() => {
        loadNotifications();

        const timer = setInterval(() => {
            // Nothing to refresh for a tab nobody is looking at
            if (document.hidden) return;
            loadNotifications();
        }, REFRESH_INTERVAL_MS);

        // Coming back to a tab that was hidden, the list can be a whole
        // background stretch out of date - catch it up straight away instead of
        // waiting out the rest of the interval
        const onVisibilityChange = () => {
            if (!document.hidden) loadNotifications();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [loadNotifications]);

    const unreadCount = notifications.filter((n) => n.unread).length;

    // Each store marks its own rows read
    const sendRead = (notif) => {
        if (!notif) return;
        if (notif.source === 'task') {
            dispatch(readTaskNotification_action(notif.taskRecipientID)).catch(() => { });
        } else {
            const notifId = notif.intimationNotificationID ?? notif.id;
            dispatch(readNotification_action(notifId)).catch(() => { });
        }
    };

    const markAllRead = () => {
        const unread = notifications.filter((n) => n.unread);

        unread.forEach((n) => locallyReadIds.current.add(n.id));

        // One call clears every unread task notification of this user, so the
        // per-item calls are only needed for the intimation side
        if (unread.some((n) => n.source === 'task')) {
            dispatch(readTaskNotification_action(null)).catch(() => { });
        }
        unread.filter((n) => n.source !== 'task').forEach(sendRead);

        setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    };

    const markOneRead = (id) => {
        locallyReadIds.current.add(id);
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
        );
        sendRead(notifications.find((n) => n.id === id));
    };

    const openPreview = (id) => {
        const notif = notifications.find((n) => n.id === id);
        if (!notif) return;

        // Open immediately. If the list item has no HTML body, fetch the full
        // body (with tables) from the preview endpoint and fill it in.
        const needsFetch =
            !notif.body &&
            (notif.source === 'task'
                ? notif.taskRecipientID != null
                : notif.feaIntimationRecipientID != null);

        setPreviewItem({ ...notif, bodyLoading: needsFetch });

        // Mark as read when preview opens
        locallyReadIds.current.add(id);
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
        );
        sendRead(notif);

        if (!needsFetch) return;

        const request =
            notif.source === 'task'
                ? getTaskNotificationPreview_action(notif.taskRecipientID)
                : getNotificationPreview_action(notif.feaIntimationRecipientID);

        dispatch(request)
            .then((res) => {
                const preview = extractPreview(res);
                // Only update if this preview is still the one being shown.
                setPreviewItem((prev) =>
                    prev && prev.id === id
                        ? {
                            ...prev,
                            body: preview.body,
                            // The list response may not carry these; fall back to the preview.
                            exhRegistrationNo: prev.exhRegistrationNo ?? preview.exhRegistrationNo,
                            companyName: prev.companyName ?? preview.companyName,
                            bodyLoading: false,
                        }
                        : prev
                );
            })
            .catch(() => {
                setPreviewItem((prev) =>
                    prev && prev.id === id ? { ...prev, bodyLoading: false } : prev
                );
            });
    };

    const closePreview = () => setPreviewItem(null);

    const openDrawer = () => setShowDrawer(true);
    const closeDrawer = () => setShowDrawer(false);

    return { notifications, unreadCount, markAllRead, markOneRead, previewItem, openPreview, closePreview, showDrawer, openDrawer, closeDrawer };
};

export default useNotifications;
